'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faScaleBalanced,
  faShieldHalved,
  faCalendarDays,
  faMoneyBillTransfer,
  faCircleInfo,
  faChevronDown,
  faChevronUp,
  faHandHoldingHeart,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import Table from '@/components/Table/Table';
import { Column } from '@/components/Table/Table.types';
import styles from './EaVsEd.module.css';


const COMPARISON_DATA = [
  {
    feature: 'Commitment',
    ea: 'Non-binding',
    ed: 'Binding (legally/contractually)',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
  {
    feature: 'Number of Schools',
    ea: 'Multiple schools',
    ed: 'Only 1 school',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
  {
    feature: 'Deadline',
    ea: 'Usually Nov 1 or Nov 15',
    ed: 'Usually Nov 1 or Nov 15',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
  {
    feature: 'Notification',
    ea: 'Dec or Jan',
    ed: 'Dec or Jan',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
  {
    feature: 'Decision Deadline',
    ea: 'May 1 (National Decision Day)',
    ed: 'Immediately upon acceptance',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
  {
    feature: 'Financial Aid Risk',
    ea: 'Low: Compare multiple offers before choosing',
    ed: 'Higher: Must accept aid without comparing (unless demonstrably insufficient)',
    eaColor: '#50e3c2',
    edColor: '#e24a4a',
  },
];

const columns: Column<any>[] = [
  {
    key: 'feature',
    title: 'Feature',
    render: (row: any) => (
      <td className={styles.cell}>
        <strong>{row.feature}</strong>
      </td>
    ),
  },
  {
    key: 'ea',
    title: 'Early Action (EA)',
    render: (row: any) => (
      <td className={styles.cell}>
        <span className={styles.badge} style={{ backgroundColor: row.eaColor }}>
          {row.ea}
        </span>
      </td>
    ),
  },
  {
    key: 'ed',
    title: 'Early Decision (ED)',
    render: (row: any) => (
      <td className={styles.cell}>
        <span className={styles.badge} style={{ backgroundColor: row.edColor }}>
          {row.ed}
        </span>
      </td>
    ),
  },
];

const DECISION_GRID = [
  {
    title: 'When to Choose Early Action',
    items: [
      'You want to apply to multiple schools',
      'You want to compare financial aid offers before committing',
      'You are not ready to commit to one school by November',
      'You want until May 1 to make your final decision',
    ],
    color: '#50e3c2',
  },
  {
    title: 'When to Choose Early Decision',
    items: [
      'You have one clear first-choice school',
      'You are confident about the financial aid package',
      'You want the highest admissions chance (higher acceptance rate typically)',
      'You and your family are ready to commit immediately upon acceptance',
    ],
    color: '#e24a4a',
  },
];

const KEY_TERMS = [
  {
    term: 'Early Decision (ED)',
    definition:
      'Binding. When applying Early Decision, you, your parent, and your high school counselor sign an agreement stating that if the college admits you, you must attend. You must immediately withdraw all applications to other colleges and submit an enrollment deposit. You can only apply to one school via Early Decision.',
  },
  {
    term: 'Early Action (EA)',
    definition:
      'Non-binding. Early Action allows you to submit your application early (typically in October or November) and receive an admissions decision early (typically December or January), but you are not obligated to attend. You have until the standard national decision deadline (May 1) to compare financial aid offers and make your final choice.',
  },
];

export default function EaVsEdPage() {
  const [activeTerm, setActiveTerm] = useState<string | null>(null);

  return (
    <div className={styles.container}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1>
            <FontAwesomeIcon icon={faScaleBalanced} /> Early Decision vs Early Action
          </h1>
          <p>
            Understanding the key differences is critical to your college
            application strategy. Choose wisely — your decision impacts your
            financial aid, deadlines, and enrollment commitments.
          </p>
          <Link href="/timeline" className={styles.ctaButton}>
            View Full Timeline <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>

      {/* Comparison Matrix */}
      <section className={styles.section}>
        <div className={styles.containerNarrow}>
          <h2 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faShieldHalved} /> Comparison Matrix
          </h2>
          <p className={styles.sectionDescription}>
            Side-by-side comparison of Early Action and Early Decision across all
            key dimensions.
          </p>
          <Table
            data={COMPARISON_DATA}
            columns={columns}
            keyField="feature"
            emptyState="No comparison data available"
            bordered={true}
            hoverable={true}
          />
        </div>
      </section>

      {/* Decision Guide */}
      <section className={styles.guideSection}>
        <div className={styles.containerNarrow}>
          <h2 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faCircleInfo} /> Decision Guide
          </h2>
          <div className={styles.guideGrid}>
            {DECISION_GRID.map((group, idx) => (
              <div
                key={idx}
                className={styles.guideCard}
                style={{ borderTopColor: group.color }}
              >
                <h3 style={{ color: group.color }}>{group.title}</h3>
                <ul>
                  {group.items.map((item, i) => (
                    <li key={i}>
                      <FontAwesomeIcon icon={faHandHoldingHeart} className={styles.checkIcon} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Terms */}
      <section className={styles.termsSection}>
        <div className={styles.containerNarrow}>
          <h2 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faCircleInfo} /> Key Terms Explained
          </h2>
          <div className={styles.termsList}>
            {KEY_TERMS.map((term, idx) => (
              <details
                key={idx}
                className={styles.termCard}
                open={activeTerm === String(idx)}
                onToggle={(e) => {
                  const target = e.currentTarget as HTMLDetailsElement;
                  setActiveTerm(target.open ? String(idx) : null);
                }}
              >
                <summary className={styles.termHeader}>
                  <h3>{term.term}</h3>
                  <FontAwesomeIcon
                    icon={activeTerm === String(idx) ? faChevronUp : faChevronDown}
                    className={styles.termChevron}
                  />
                </summary>
                <p className={styles.termBody}>{term.definition}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.containerNarrow}>
          <h2>Need Help Deciding?</h2>
          <p>
            Our advisors can help you navigate the Early Decision vs Early Action
            decision and create the best application strategy for your situation.
          </p>
          <Link href="/sessions" className={styles.ctaButton}>
            Book a Session <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>
    </div>
  );
}
